import React from 'react';
import { useTranslation } from 'react-i18next';

interface VideoOptimizationProps {
  isOptimizing: boolean;
  optimizationResult: any;
  showOptimizationResult: boolean;
  setShowOptimizationResult: (show: boolean) => void;
  handleAutoOptimize: () => void;
  applyOptimizedParams: (type: 'basic' | 'advanced') => void;
  copyOptimizationResult: () => void;
  videoPrompt: string;
}

const VideoOptimization: React.FC<VideoOptimizationProps> = ({
  isOptimizing,
  optimizationResult,
  showOptimizationResult,
  setShowOptimizationResult,
  handleAutoOptimize,
  applyOptimizedParams,
  copyOptimizationResult,
  videoPrompt,
}) => {
  const { t } = useTranslation('aiGenerator');
  const basicOpt = optimizationResult?.basic_optimization;
  const advancedOpt = optimizationResult?.advanced_optimization;
  const enhancedPrompt = optimizationResult?.enhanced_prompt;

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
        <button
          onClick={handleAutoOptimize}
          disabled={isOptimizing}
          style={{
            backgroundColor: isOptimizing ? '#666' : optimizationResult ? '#6366f1' : '#10b981',
            border: 'none',
            borderRadius: '6px',
            padding: '10px 16px',
            color: 'white',
            fontSize: '14px',
            cursor: isOptimizing ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isOptimizing ? (
            <>
              <div style={{
                width: '14px',
                height: '14px',
                border: '2px solid #ffffff40',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              {t('video.optimization.analyzing')}
            </>
          ) : optimizationResult ? (
            <>{t('video.optimization.regenerate')}</>
          ) : (
            <>{t('video.optimization.autoOptimize')}</>
          )}
        </button>
        
        {optimizationResult && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {/* Basic Apply Button */}
            {basicOpt && (
              <button
                onClick={() => {
                  console.log('🎯 BASIC APPLY BUTTON CLICKED');
                  applyOptimizedParams('basic');
                }}
                style={{
                  backgroundColor: '#22c55e',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: 'white',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={`Basic: ${basicOpt.duration}s, ${basicOpt.quality}, ${basicOpt.aspect_ratio}`}
              >
                ✅ Apply Basic
              </button>
            )}
            
            {/* Advanced Apply Button */}
            {advancedOpt && (
              <button
                onClick={() => {
                  console.log('🎯 ADVANCED APPLY BUTTON CLICKED');
                  applyOptimizedParams('advanced');
                }}
                style={{
                  backgroundColor: '#8b5cf6',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  color: 'white',
                  fontSize: '12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={`Advanced: ${advancedOpt.duration}s, ${advancedOpt.quality}, ${advancedOpt.aspect_ratio} + Motion Controls`}
              >
                🎛️ Apply Advanced
              </button>
            )}
            
            <button
              onClick={() => setShowOptimizationResult(!showOptimizationResult)}
              style={{
                backgroundColor: '#374151',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 12px',
                color: 'white',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              {showOptimizationResult ? '👁️ Hide' : '📊 View'}
            </button>
          </div>
        )}
      </div>
      
      {showOptimizationResult && optimizationResult && (
        <div style={{
          backgroundColor: '#0f1419',
          border: '1px solid #374151',
          borderRadius: '8px',
          padding: '15px',
          fontSize: '14px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <strong style={{ color: '#10b981', fontSize: '16px' }}>🤖 Optimization Recommendations</strong>
            <button
              onClick={copyOptimizationResult}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid #374151',
                borderRadius: '4px',
                padding: '4px 8px',
                color: '#ccc',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              📋 Copy
            </button>
          </div>
          
          {/* Enhanced Prompt Display */}
          {enhancedPrompt && (
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ color: '#fbbf24', margin: '0 0 8px 0', fontSize: '14px' }}>
                ✨ Enhanced Prompt:
              </h4>
              <div style={{ 
                background: 'rgba(251, 191, 36, 0.1)', 
                border: '1px solid rgba(251, 191, 36, 0.3)',
                borderRadius: '6px',
                padding: '10px',
                color: '#fde047',
                fontSize: '13px',
                fontStyle: 'italic',
                lineHeight: '1.4'
              }}>
                "{enhancedPrompt}"
              </div>
            </div>
          )}

          {/* Optimization Options Comparison */}
          {(basicOpt || advancedOpt) && (
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ color: '#60a5fa', margin: '0 0 10px 0', fontSize: '14px' }}>
                📊 Choose Optimization Level:
              </h4>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                {/* Basic Option */}
                {basicOpt && (
                  <div style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    <div style={{ color: '#22c55e', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                      ✅ Basic Optimization
                    </div>
                    <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.4' }}>
                      • Duration: {basicOpt.duration}s<br/>
                      • Quality: {basicOpt.quality}<br/>
                      • Aspect: {basicOpt.aspect_ratio}
                    </div>
                  </div>
                )}
                
                {/* Advanced Option */}
                {advancedOpt && (
                  <div style={{
                    background: 'rgba(139, 92, 246, 0.1)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    <div style={{ color: '#8b5cf6', fontWeight: '600', marginBottom: '8px', fontSize: '13px' }}>
                      🎛️ Advanced Optimization
                    </div>
                    <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.4' }}>
                      • Duration: {advancedOpt.duration}s<br/>
                      • Quality: {advancedOpt.quality}<br/>
                      • Aspect: {advancedOpt.aspect_ratio}<br/>
                      {advancedOpt.advanced_runway && (
                        <>
                          • Motion Types: {advancedOpt.advanced_runway.motion_types?.length || 0}<br/>
                          • Template: {advancedOpt.advanced_runway.motion_template}<br/>
                          • Intensity: {advancedOpt.advanced_runway.motion_intensity}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Recommendations */}
          {optimizationResult.recommendations && optimizationResult.recommendations.length > 0 ? (
            <div>
              <h4 style={{ color: '#8b5cf6', margin: '0 0 10px 0', fontSize: '14px' }}>
                💡 Suggestions to improve your video:
              </h4>
              <ul style={{ margin: '0', paddingLeft: '20px', color: '#e5e7eb', lineHeight: '1.6' }}>
                {optimizationResult.recommendations.map((rec, index) => (
                  <li key={index} style={{ marginBottom: '8px' }}>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div style={{ color: '#e5e7eb', fontStyle: 'italic' }}>
              ✅ Your current settings look good! No specific recommendations at this time.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoOptimization;